import { combineAndDedupArrays, insertArrayItem, updateObject } from 'shared/utility';
import * as actionTypes from 'store/actions/actionTypes';

const initialState = {
  error: null,
  loading: false,
  deleting: false,
  submitted: true,
  postImages: [],
  removedImageIds: [],
  addComment: {
    images: [],
    removedImageIds: [],
  },
  replyToComment: {
    images: [],
    removedImageIds: [],
  },
  editComment: {
    images: [],
    removedImageIds: [],
  },
  adminNote: {
    images: [],
    removedImageIds: [],
  },
  fetchedPost: null,
  fetchedPreviewPost: null,
  fetchedPostIsGift: false,
  submittedPostIsGift: false,
  isDraft: false,
  newPost: false,
  postHasBeenEdited: false,
  imagesUpdated: false,
  commentError: null,
  commentAdded: false,
  redirectPath: null,
  showPostForm: false,
  showPostDetailNavOptions: false,
  returnedToPostFormFromPreview: false,
};

const initPostForm = (state, action) => {
  return updateObject(state, {
    submitted: false,
    postImages: action.PostId !== null ? state.postImages : [],
    newPost: !action.PostId,
    fetchedPostIsGift: !!action.isGift,
    isDraft: action.isDraft,
    deleted: false,
    imagesUpdated: false,
    showPostForm: true,
    fetchedPost: action.PostId !== null ? state.fetchedPost : null,
    loading: false,
    redirectPath: action.redirectPath,
    returnedToPostFormFromPreview: false,
  });
};

const closePostForm = (state) => {
  return updateObject(state, {
    showPostForm: false,
    fetchedPostIsGift: false,
    isDraft: false,
    submitted: false,
    fetchedPreviewPost: null,
    postImages: [],
    newPost: false,
    deleted: false,
    imagesUpdated: false,
    redirectPath: null,
    returnedToPostFormFromPreview: false,
  });
};

const addPostStart = (state) => {
  return updateObject(state, {
    loading: true,
    submitted: false,
    submittedPostIsGift: false,
  });
};

const addPostFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
    removedPostImages: [],
    redirectPath: null,
  });
};

const addPostSuccess = (state, action) => {
  return updateObject(state, {
    loading: false,
    submitted: true,
    newPost: false,
    isDraft: action.isDraft,
    fetchedPost: action.isDraft ? state.fetchedPost : null,
    fetchedPreviewPost: null,
    postImages: [],
    redirectPath: null,
    submittedPostIsGift: action.isGift === true,
  });
};

const uploadPostImageStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
  });
};

const uploadPostImageSuccess = (state, action) => {
  let updatedObject = null;
  if (action.Type === 'comment') {
    if (action.CommentType) {
      updatedObject = updateObject(state, {
        [action.CommentType]: {
          ...state[action.CommentType],
          images: combineAndDedupArrays([...state[action.CommentType].images], [action.postImage]),
        },
        error: null,
        loading: false,
        imagesUpdated: true,
      });
    }
  } else {
    updatedObject = updateObject(state, {
      postImages: combineAndDedupArrays([...state.postImages], [action.postImage]),
      error: null,
      loading: false,
      imagesUpdated: true,
    });
  }
  return updatedObject;
};

const uploadPostImageFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const removePostImage = (state, action) => {
  let updatedObject = null;
  if (action.Type === 'comment') {
    if (action.CommentType) {
      const filteredImages = state[action.CommentType].images.filter((image) => image.Id !== action.imageId);
      updatedObject = updateObject(state, {
        [action.CommentType]: {
          images: filteredImages,
          removedImageIds: insertArrayItem(
            state[action.CommentType].removedImageIds,
            state[action.CommentType].removedImageIds.length,
            action.imageId,
          ),
        },
        imagesUpdated: true,
      });
    }
  } else {
    const filteredImages = state.postImages.filter((image) => image.Id !== action.imageId);
    updatedObject = updateObject(state, {
      postImages: filteredImages,
      removedImageIds: insertArrayItem(state.removedImageIds, state.removedImageIds.length, action.imageId),
      imagesUpdated: true,
    });
  }
  return updatedObject;
};

const clearCommentImages = (state, action) => {
  return updateObject(state, {
    [action.CommentType]: {
      images: [],
      removedCommentImageIds: [],
    },
  });
};

const reorderPostImages = (state, action) => {
  return updateObject(state, {
    postImages: action.postImages,
    imagesUpdated: true,
  });
};

const fetchPostStart = (state) => {
  return updateObject(state, {
    fetchedPost: null,
    postImages: [],
    addComment: {
      images: [],
      removedImageIds: [],
    },
    error: null,
    loading: true,
    submitted: true,
  });
};

const fetchPostSuccess = (state, action) => {
  const postObject = action.Post;
  postObject.Rooms = action.Rooms;
  return updateObject(state, {
    fetchedPost: postObject,
    postImages: action.Post.Files.Files.length > 0 ? action.Post.Files.Files : [],
    error: null,
    loading: false,
    submitted: false,
    postHasBeenEdited: false,
  });
};

const fetchPostFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const editPostStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
  });
};

const editPostFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const editPostSuccess = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: null,
    submitted: true,
    newPost: false,
    isDraft: action.isDraft,
    fetchedPost: null,
    fetchedPreviewPost: null,
    postImages: [],
    redirectPath: null,
    postHasBeenEdited: true,
  });
};

const addCommentImageToPostStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
  });
};

const addCommentImageToPostFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const addCommentImageToPostSuccess = (state) => {
  return updateObject(state, {
    loading: false,
    error: null,
    submitted: true,
  });
};

const deletePostStart = (state) => {
  return updateObject(state, {
    loading: true,
    deleted: false,
    deleting: true,
    error: null,
    submitted: false,
  });
};

const deletePostFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
    deleting: false,
  });
};

const deletePostSuccess = (state) => {
  return updateObject(state, {
    deleted: true,
    loading: false,
    error: null,
    submitted: true,
    newPost: false,
    deleting: false,
  });
};

const submitPostCommentStart = (state) => {
  return updateObject(state, {
    loading: true,
    commentError: null,
    commentAdded: false,
  });
};

const submitPostCommentSuccess = (state) => {
  return updateObject(state, {
    loading: false,
    commentError: null,
    commentAdded: true,
    addComment: {
      images: [],
      removedImageIds: [],
    },
    replyToComment: {
      images: [],
      removedImageIds: [],
    },
    editComment: {
      images: [],
      removedImageIds: [],
    },
    adminNote: {
      images: [],
      removedImageIds: [],
    },
  });
};

const submitPostCommentFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    commentError: action.error,
    addComment: {
      images: [],
      removedImageIds: [],
    },
    replyToComment: {
      images: [],
      removedImageIds: [],
    },
    editComment: {
      images: [],
      removedImageIds: [],
    },
    adminNote: {
      images: [],
      removedImageIds: [],
    },
  });
};

const entryCommentUpdateStart = (state) => {
  return updateObject(state, {
    loading: true,
    commentError: null,
    commentAdded: false,
  });
};

const entryCommentUpdateSuccess = (state) => {
  return updateObject(state, {
    loading: false,
    commentAdded: true,
    addComment: {
      images: [],
      removedImageIds: [],
    },
    replyToComment: {
      images: [],
      removedImageIds: [],
    },
    editComment: {
      images: [],
      removedImageIds: [],
    },
    adminNote: {
      images: [],
      removedImageIds: [],
    },
  });
};

const entryCommentUpdateFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    commentError: action.error,
    addComment: {
      images: [],
      removedImageIds: [],
    },
    replyToComment: {
      images: [],
      removedImageIds: [],
    },
    editComment: {
      images: [],
      removedImageIds: [],
    },
    adminNote: {
      images: [],
      removedImageIds: [],
    },
  });
};

const setPreviewPost = (state, action) => {
  return updateObject(state, {
    fetchedPreviewPost: action.Post,
    showPostForm: false,
    returnedToPostFormFromPreview: false,
    fetchedPostIsGift: true,
  });
};

const returnToPostForm = (state, action) => {
  return updateObject(state, {
    showPostForm: true,
    returnedToPostFormFromPreview: true,
    fetchedPostIsGift: action.isGift === true,
  });
};

const togglePostDetailNavOptions = (state) => {
  return updateObject(state, {
    showPostDetailNavOptions: !state.showPostDetailNavOptions,
  });
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.INIT_POST_FORM:
      return initPostForm(state, action);
    case actionTypes.UPLOAD_POST_IMAGE_START:
      return uploadPostImageStart(state, action);
    case actionTypes.UPLOAD_POST_IMAGE_SUCCESS:
      return uploadPostImageSuccess(state, action);
    case actionTypes.UPLOAD_POST_IMAGE_FAILED:
      return uploadPostImageFailed(state, action);
    case actionTypes.REMOVE_POST_IMAGE:
      return removePostImage(state, action);
    case actionTypes.CLEAR_COMMENT_IMAGES:
      return clearCommentImages(state, action);
    case actionTypes.REORDER_POST_IMAGES:
      return reorderPostImages(state, action);
    case actionTypes.ADD_POST_START:
      return addPostStart(state, action);
    case actionTypes.ADD_POST_SUCCESS:
      return addPostSuccess(state, action);
    case actionTypes.ADD_POST_FAILED:
      return addPostFailed(state, action);
    case actionTypes.FETCH_POST_START:
      return fetchPostStart(state, action);
    case actionTypes.FETCH_POST_SUCCESS:
      return fetchPostSuccess(state, action);
    case actionTypes.FETCH_POST_FAILED:
      return fetchPostFailed(state, action);
    case actionTypes.SET_PREVIEW_POST:
      return setPreviewPost(state, action);
    case actionTypes.RETURN_TO_GIFT_POST_FORM:
      return returnToPostForm(state, action);
    case actionTypes.EDIT_POST_START:
      return editPostStart(state, action);
    case actionTypes.EDIT_POST_SUCCESS:
      return editPostSuccess(state, action);
    case actionTypes.EDIT_POST_FAILED:
      return editPostFailed(state, action);
    case actionTypes.ADD_COMMENT_IMAGE_TO_POST_START:
      return addCommentImageToPostStart(state, action);
    case actionTypes.ADD_COMMENT_IMAGE_TO_POST_SUCCESS:
      return addCommentImageToPostSuccess(state, action);
    case actionTypes.ADD_COMMENT_IMAGE_TO_POST_FAILED:
      return addCommentImageToPostFailed(state, action);
    case actionTypes.CLOSE_POST_FORM:
      return closePostForm(state, action);
    case actionTypes.DELETE_POST_START:
      return deletePostStart(state, action);
    case actionTypes.DELETE_POST_FAILED:
      return deletePostFailed(state, action);
    case actionTypes.DELETE_POST_SUCCESS:
      return deletePostSuccess(state, action);
    case actionTypes.SUBMIT_POST_COMMENT_START:
      return submitPostCommentStart(state, action);
    case actionTypes.SUBMIT_POST_COMMENT_SUCCESS:
      return submitPostCommentSuccess(state, action);
    case actionTypes.SUBMIT_POST_COMMENT_FAILED:
      return submitPostCommentFailed(state, action);
    case actionTypes.ENTRY_COMMENT_UPDATE_START:
      return entryCommentUpdateStart(state, action);
    case actionTypes.ENTRY_COMMENT_UPDATE_SUCCESS:
      return entryCommentUpdateSuccess(state, action);
    case actionTypes.ENTRY_COMMENT_UPDATE_FAILED:
      return entryCommentUpdateFailed(state, action);
    case actionTypes.TOGGLE_POST_DETAIL_NAV_OPTIONS:
      return togglePostDetailNavOptions(state, action);
    default:
      return state;
  }
};

export default reducer;
